import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Typography,
  Box,
  Avatar,
  ListItemButton,
  useMediaQuery,
  useTheme,
  List,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Pagination,
  Select,
  MenuItem,
} from "@mui/material";
import { BusinessCenterRounded } from "@mui/icons-material";
import moment from "moment";
import { getSolrJobsAction, selectedJobAction } from "../redux/action";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { appSelector } from "../redux/selector";
import generateEdismaxSolrQuery from "../../helpers/solredismax";
import LoadingSkeleton from "../loadingSkeletons/LoadingList";
import { getMappedDates } from "../utils/utils";
import { resumeBuilderSelector } from "../OptimiseResume/ResumeBuilderActions/selector";
import { GET_SOLR_JOBS_LOADING_ACTION } from "../redux/actionTypes";

const Candidatejobslist = ({
  onSelectJob,
  locationSearchTerm,
  searchTerm,
  selectedDayFilter,
  selectedJobtypeFilter,
  jobMobileViewHandler,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const pendingJobIdFromNav = location.state?.selectedJobId;
  const { candidateDetails, selectedJob, jobtype_jobids, solrJobsLoading } =
    useSelector(appSelector);
  const { currentSection } = useSelector(resumeBuilderSelector);
  const [jobs, setJobs] = useState([]);
  const [limit, setLimit] = useState(20);
  const theme = useTheme();
  const hasLoadedJobsListRef = useRef(false);
  const [hasLoadedJobsList, setHasLoadedJobsList] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const lastSolrUrlRef = useRef("");
  const prevFilterKeyRef = useRef("");
  const prevSolrJobsLoadingRef = useRef(solrJobsLoading);
  const appliedPendingJobRef = useRef(false);
  const lastPendingJobIdRef = useRef(null);
  const selectedJobItemRef = useRef(null);
  const isDesktopXS = useMediaQuery(
    "(min-width: 992px) and (max-width: 1199px)"
  );
  const isDesktopLarge = useMediaQuery(theme.breakpoints.up("lg"));
  const isDesktopSmall = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1439px)"
  );
  const isDesktopMedium = useMediaQuery(
    "(min-width: 1440px) and (max-width: 1919px)"
  );

  const candidateId = candidateDetails?.candidate_id;
  const relatedTitles = candidateDetails?.related_titles || "";
  const candidateRole = candidateDetails?.role || "";
  const jobtypeKey = Array.isArray(jobtype_jobids)
    ? jobtype_jobids.join(",")
    : "";

  const filterKey = useMemo(
    () =>
      [
        candidateId,
        relatedTitles,
        candidateRole,
        searchTerm || "",
        selectedDayFilter || "",
        selectedJobtypeFilter || "",
        locationSearchTerm || "",
        jobtypeKey,
      ].join("|"),
    [
      candidateId,
      relatedTitles,
      candidateRole,
      searchTerm,
      selectedDayFilter,
      selectedJobtypeFilter,
      locationSearchTerm,
      jobtypeKey,
    ]
  );

  // On remount, mark Redux loading. Keep selection if opened from dashboard/chat job click.
  useEffect(() => {
    if (!hasLoadedJobsListRef.current) {
      dispatch({ type: GET_SOLR_JOBS_LOADING_ACTION });
      if (!pendingJobIdFromNav) {
        dispatch(selectedJobAction(null));
      }
    }
  }, [dispatch, pendingJobIdFromNav]);

  // Allow a new chat/dashboard job id to apply (manual clicks set applied=true)
  useEffect(() => {
    if (
      pendingJobIdFromNav &&
      String(pendingJobIdFromNav) !== String(lastPendingJobIdRef.current)
    ) {
      lastPendingJobIdRef.current = pendingJobIdFromNav;
      appliedPendingJobRef.current = false;
    }
    if (!pendingJobIdFromNav) {
      lastPendingJobIdRef.current = null;
    }
  }, [pendingJobIdFromNav]);

  useEffect(() => {
    if (prevSolrJobsLoadingRef.current && !solrJobsLoading) {
      hasLoadedJobsListRef.current = true;
      setHasLoadedJobsList(true);
    }
    prevSolrJobsLoadingRef.current = solrJobsLoading;
  }, [solrJobsLoading]);

  const showInitialJobsLoading = !hasLoadedJobsList;

  const setSolrJobsState = async (solrURL) => {
    if (!solrURL) return;
    if (lastSolrUrlRef.current === solrURL) return;

    lastSolrUrlRef.current = solrURL;
    const res = await dispatch(getSolrJobsAction(solrURL));

    const payload = res?.data?.jobs ?? res?.jobs ?? res;

    const response = payload?.response ?? payload ?? {};
    const docs =
      response?.docs ?? (Array.isArray(payload) ? payload : []) ?? [];

    const totalResults =
      response?.numFound ?? payload?.numFound ?? res?.count ?? docs.length;

    setJobs(docs);
    setTotalPages(Math.max(1, Math.ceil(totalResults / limit)));
  };

  // Prefer one-shot redirect selection (chat / dashboard), then list selection.
  useEffect(() => {
    if (!jobs?.length) return;

    const pendingJobId =
      !appliedPendingJobRef.current && pendingJobIdFromNav
        ? pendingJobIdFromNav
        : null;

    // One-shot: select job from chat/dashboard navigation, then clear nav state
    if (pendingJobId) {
      const match = jobs.find(
        (job) => String(job?.id) === String(pendingJobId)
      );
      if (match) {
        if (String(selectedJob?.id) !== String(match.id)) {
          dispatch(selectedJobAction(match));
        }
        appliedPendingJobRef.current = true;
        navigate(location.pathname, { replace: true, state: {} });
        return;
      }
      // Keep Redux job from redirect until it appears in this list page
      if (String(selectedJob?.id) === String(pendingJobId)) {
        return;
      }
    }

    if (currentSection?.isRedirect === true) {
      const targetId = selectedJob?.id;
      const match = targetId
        ? jobs.find((job) => String(job?.id) === String(targetId))
        : null;
      if (match) {
        if (String(selectedJob?.id) !== String(match.id)) {
          dispatch(selectedJobAction(match));
        }
        return;
      }
    }

    const match = selectedJob?.id
      ? jobs.find((job) => String(job?.id) === String(selectedJob?.id))
      : null;

    if (match) {
      if (String(selectedJob?.id) !== String(match.id)) {
        dispatch(selectedJobAction(match));
      }
      return;
    }

    if (String(selectedJob?.id) === String(jobs[0]?.id)) return;

    dispatch(selectedJobAction(jobs[0]));
  }, [
    jobs,
    selectedJob?.id,
    currentSection?.isRedirect,
    pendingJobIdFromNav,
    dispatch,
    navigate,
    location.pathname,
  ]);

  // Scroll the selected job into view after coming from dashboard
  useEffect(() => {
    if (!selectedJob?.id || !hasLoadedJobsList) return;
    selectedJobItemRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });
  }, [selectedJob?.id, hasLoadedJobsList, jobs]);

  const getSolrQuery = (
    _ignored,
    searchTermArg,
    dayCount,
    selectedJobtypeFilterArg,
    locationSearchTermArg
  ) => {
    const start = Math.max(0, (page - 1) * limit);
    const daysFilter = dayCount ? getMappedDates(dayCount) : "[* TO *]";

    let exactTitles = [];

    if (relatedTitles) {
      exactTitles = exactTitles.concat(relatedTitles.split(","));
    }
    if (candidateRole) {
      exactTitles.push(candidateRole);
    }
    const cleanedTitles = exactTitles
      .filter(Boolean)
      .map((title) => `"${title.trim()}"`);

    const combinedString = cleanedTitles.join(" OR ");

    const baseParams = {
      jobtitle: `(${combinedString})`,
      posted_date: daysFilter,
    };
    if (locationSearchTermArg?.trim()) {
      baseParams.location = `"${locationSearchTermArg.trim()}"`;
    }

    const sort_key = searchTermArg
      ? `if(exists(query({!v='jobtitle:${searchTermArg}'})), 0, 1) asc, posted_date%20desc&wt=json`
      : `posted_date desc&wt=json`;

    if (searchTermArg) {
      return generateEdismaxSolrQuery(baseParams, start, limit, sort_key);
    }

    if (selectedJobtypeFilterArg && jobtype_jobids?.length) {
      const orIds = jobtype_jobids.map((id) => id).join(" OR ");
      const jobTypeParams = { id: `(${orIds})`, posted_date: daysFilter };
      if (locationSearchTermArg?.trim()) {
        jobTypeParams.location = `"${locationSearchTermArg.trim()}"`;
      }
      return generateEdismaxSolrQuery(
        jobTypeParams,
        start,
        limit,
        `posted_date desc&wt=json`
      );
    }

    return generateEdismaxSolrQuery(baseParams, start, limit, sort_key);
  };

  // Single Solr fetch path. Filter changes reset to page 1; page/limit changes fetch.
  useEffect(() => {
    if (!candidateId) return;

    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      lastSolrUrlRef.current = "";
      if (page !== 1) {
        setPage(1);
        return;
      }
    }

    const solrURL = getSolrQuery(
      0,
      searchTerm,
      selectedDayFilter,
      selectedJobtypeFilter,
      locationSearchTerm
    );
    setSolrJobsState(solrURL);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, filterKey, candidateId]);

  const handleLimitChange = (e) => {
    lastSolrUrlRef.current = "";
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const HighlightedCompanyName = ({ companyName }) => {
    const highlightLetters = (text, search) => {
      if (!search || !text) {
        return <span>{text || ""}</span>;
      }

      const regex = new RegExp(`(${search})`, "ig");
      const parts = text.split(regex);

      return parts.map((part, index) =>
        regex.test(part) ? (
          <span
            key={index}
            style={{
              color: "#000",
              backgroundColor: "#ff0",
              fontWeight: 600,
            }}
          >
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        )
      );
    };

    const localTheme = useTheme();
    const localIsDesktopLarge = useMediaQuery(localTheme.breakpoints.up("lg"));

    return (
      <Typography
        noWrap
        sx={{
          fontSize: localIsDesktopLarge ? "12px" : "9px",
          fontWeight: 500,
          margin: 0,
          minWidth: 0,
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {highlightLetters(companyName)}
      </Typography>
    );
  };

  const getTimeAgo = (timestamp) => {
    const lastModifiedMoment = moment(timestamp);
    const now = moment();
    const diffInSeconds = now.diff(lastModifiedMoment, "seconds");

    if (diffInSeconds < 60) {
      return "a few seconds ago";
    } else {
      return lastModifiedMoment.fromNow();
    }
  };

  return showInitialJobsLoading ? (
    <LoadingSkeleton />
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "hidden",
          minHeight: 0,
        }}
      >
        <List sx={{ padding: 0, margin: 0 }}>
          {jobs?.length > 0 ? (
            jobs.map((job, index) => (
              <ListItemButton
                key={job?.id ?? index}
                ref={
                  String(selectedJob?.id) === String(job?.id)
                    ? selectedJobItemRef
                    : null
                }
                selected={String(selectedJob?.id) === String(job?.id)}
                onClick={() => {
                  // Manual click wins over any leftover chat/dashboard pending id
                  appliedPendingJobRef.current = true;
                  if (location.state?.selectedJobId) {
                    navigate(location.pathname, { replace: true, state: {} });
                  }
                  dispatch(selectedJobAction(job));
                  jobMobileViewHandler(false);
                }}
                sx={{
                  ".hide-icons": {
                    display: "none",
                  },
                  "&:hover": {
                    ".hide-icons": {
                      display: "block",
                    },
                  },
                  padding: isDesktopLarge
                    ? "10px 20px"
                    : isDesktopMedium
                      ? "8px 16px"
                      : isDesktopSmall
                        ? "6px 12px"
                        : "6px 12px",
                }}
              >
                <div style={{ display: "flex", gap: 10, width: "100%" }}>
                  <Avatar
                    sx={{
                      height: isDesktopLarge
                        ? "40px"
                        : isDesktopMedium
                          ? "35px"
                          : isDesktopSmall
                            ? "32px"
                            : "28px",
                      width: isDesktopLarge
                        ? "40px"
                        : isDesktopMedium
                          ? "35px"
                          : isDesktopSmall
                            ? "32px"
                            : "28px",
                    }}
                  >
                    <BusinessCenterRounded
                      sx={{
                        color: "#fff",
                        height: isDesktopLarge
                          ? "24px"
                          : isDesktopMedium
                            ? "20px"
                            : isDesktopSmall
                              ? "16px"
                              : "14px",
                        width: isDesktopLarge
                          ? "24px"
                          : isDesktopMedium
                            ? "22px"
                            : isDesktopSmall
                              ? "18px"
                              : "16px",
                      }}
                    />
                  </Avatar>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      width: "100%",
                      minWidth: 0,
                    }}
                  >
                    <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
                      <Tooltip title={job?.jobtitle || ""}>
                        <Typography
                          noWrap
                          sx={{
                            fontSize: isDesktopXS ? "10px" : "12px",
                            fontWeight: 600,
                            margin: 0,
                            display: "block",
                            maxWidth: "100%",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {job?.jobtitle}
                        </Typography>
                      </Tooltip>
                      <Box sx={{ minWidth: 0 }}>
                        <HighlightedCompanyName
                          companyName={job?.company_name}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                          width: "100%",
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontSize: isDesktopXS ? "9px" : "11px",
                            margin: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          {job.location || job.remote
                            ? job.location
                              ? `${job.location}${job.remote ? " " : ""}`
                              : "Remote"
                            : "N/A"}
                        </Typography>

                        <Typography
                          sx={{
                            margin: 0,
                            color: "#757575",
                            fontWeight: 500,
                            fontSize: isDesktopLarge ? "0.65rem" : "0.60rem",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                            textAlign: "right",
                            ml: 2,
                          }}
                        >
                          {job && getTimeAgo(job.posted_date)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </div>
              </ListItemButton>
            ))
          ) : (
            <Card>
              <CardContent sx={{ display: "flex", justifyContent: "center " }}>
                <Typography variant="body2" component="h2" gutterBottom>
                  No Jobs Found
                </Typography>
              </CardContent>
            </Card>
          )}
        </List>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 1,
          borderTop: "1px solid rgba(0, 0, 0, 0.12)",
          bgcolor: "#fff",
          flexShrink: 0,
        }}
      >
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_, v) => {
            lastSolrUrlRef.current = "";
            setPage(v);
          }}
          color="primary"
          size="small"
          sx={{ display: "flex", justifyContent: "center", mb: 1 }}
          siblingCount={0}
          boundaryCount={1}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            px: 2,
          }}
        >
          <Typography variant="body2" sx={{ mr: 1 }}>
            Rows per page:
          </Typography>
          <Select
            size="small"
            variant="standard"
            value={limit}
            onChange={handleLimitChange}
            sx={{ width: 60 }}
          >
            {[10, 20, 50, 100].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    </Box>
  );
};

export default Candidatejobslist;
